import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { CodeTypeModel }                 from '../../core/models/code-type';
import { InvoiceContactStatusComponent } from '../../invoices/contact-status/invoice.contact-status.component';
import { JobRowExpandComponent }         from './transaction-center.job.row.expand.component';
import { JobsService }                   from './transaction-center.jobs.service';
import { MessagesService }               from '../../messages/messages.service';
import { MessengerService }              from '../../messages/messenger/messenger.service';
import { SessionService }                from '../../sessions/session.service';
import { Table }                         from '../../table/types/table';
import { TableQueryParameters }          from '../../table/types/table-query-parameters';
import { TableService }                  from '../../table/table.service';
import { TransactionCenterFiltersModel } from '../typings/transaction-center.filters';
import { TransactionCenterJobModel }     from './typings/transaction-center.job';
import { TransactionCenterService }      from '../transaction-center.service';

@Component({
  selector   : 'wor-transaction-center-jobs',
  template: require('./transaction-center.jobs.component.html')
})
export class TransactionCenterJobsComponent implements OnDestroy, OnInit {
  @Input() constraints     : Array<CodeTypeModel>             = [];
  @Input() jobs            : Array<TransactionCenterJobModel> = [];
  @Input() jobStatuses     : Array<CodeTypeModel>             = [];
  @Input() jobStatusOptions: Array<CodeTypeModel>             = [];

  broadcastKey  = this.jobsService.broadcastKey;
  tableCacheKey = this.jobsService.tableCacheKey;

  company        = this.sessionService.company();
  fleetCodeLabel = this.company.company_variable_fields.fleet_code_name
    || this.translateService.instant('VEHICLE_SPACE.FLEET_CODE');

  regoLabel = this.company.company_variable_fields.plate_number_name
    || this.sessionService.countrySettings().rego_label;

  filters : TransactionCenterFiltersModel = this.transactionCenterService.clearCachedWildcardDates(
    this.tableService.setFiltersFromCache(this.tableCacheKey, {
      constrainBy: '*',
      jobStatus  : '*',
      postDate   : null,
      value      : ''
    }),
    [ 'postDate' ]
  );

  tableSettings : Table = {
    broadcast: this.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.tableCacheKey
    },
    columns: [],
    empty  : {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_JOBS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.jobsService.getCount(this.filters)
    },
    rows: {
      expand: {
        bindings: {
          outputs: {
            onEditClick : ( job : TransactionCenterJobModel ) => this.viewJob(job),
            onLaborClick: ( job : TransactionCenterJobModel ) => this.router.stateService.go('app.labor-items', { invoice_id: job.job.invoice.id }),
            onSMSClick  : ( job : TransactionCenterJobModel ) => this.sendSMS(job)
          }
        },
        enabled: true,
        factory: JobRowExpandComponent
      },
      highlight: {
        enabled: true
      },
      select: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
      direction: 'desc',
      field    : 'invoice.post_date'
    })
  };

  searchSubscription: Subscription;

  constructor (
    private jobsService             : JobsService,
    private messagesService         : MessagesService,
    private messengerService        : MessengerService,
    private router                  : UIRouter,
    private sessionService          : SessionService,
    private tableService            : TableService,
    private transactionCenterService: TransactionCenterService,
    private translateService        : TranslateService
  ) {}

  ngOnDestroy () : void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnInit () : void {
    this.buildColumns();

    this.transactionCenterService.setSearch(this.filters.value || '');

    this.searchSubscription = this.transactionCenterService.searchChanges
      .subscribe(value => {
        if ( value !== null && value !== this.filters.value ) {
          this.filters.value = value;

          this.filterTable();
        }
      });
  }

  buildColumns () : void {
    this.tableSettings.columns = [
      {
        field : 'job.invoice.post_date',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DATE')
        },
        pipe: 'globalDate'
      },
      {
        field : 'job.customer.display_name',
        header: {
          text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER')
        }
      },
      {
        field : 'job.vehicle.make',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MAKE')
        }
      },
      {
        field : 'job.vehicle.model',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL')
        }
      },
      {
        field : 'job.vehicle.plate_number',
        header: {
          text: this.regoLabel
        }
      }
    ];

    if ( this.company.company_settings.show_fleet_code ) {
      this.tableSettings.columns.push({
        classes: 'text-center',
        field  : 'job.vehicle.fleet_code',
        header : {
          classes: 'text-center',
          text   : this.fleetCodeLabel
        }
      });
    }

    this.tableSettings.columns.push(
      {
        classes: 'text-center',
        field  : 'job.invoice.job_card_number',
        header : {
          classes: 'text-center',
          text   : this.translateService.instant('MENU_SPACE.JOB_CARD')
        }
      },
      {
        classes: 'text-left',
        field  : 'job.invoice.job_status',
        header : {
          classes: 'text-left',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.STATUS')
        },
        pipe    : 'jobStatus',
        pipeArgs: [ this.jobStatuses ]
      },
      {
        classes: 'text-left',
        field  : 'job.invoice.job_status_comment',
        header : {
          classes: 'text-left',
          text   : this.translateService.instant('JS_SPACE.COLUMNS.STATUS_COMMENT')
        }
      },
      {
        component: {
          enabled: true,
          factory: InvoiceContactStatusComponent
        },
        field : 'job.invoice',
        header: {
          classes: 'text-center',
          text   : this.translateService.instant('GENERAL_SPACE.STATUS.CONTACTED')
        },
        sortable: false
      },
      {
        classes: 'text-right',
        field  : 'job.invoice.total',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL')
        },
        pipe: 'globalCurrency'
      }
    );
  }

  filterTable () : void {
    if ( window[ this.broadcastKey ] ) {
      window[ this.broadcastKey ](this.filters);
    }
  }

  getJobs ( params : TableQueryParameters ) : Promise<Array<TransactionCenterJobModel>> {
    return this.jobsService.get(this.filters, params)
      .then(( jobs : Array<TransactionCenterJobModel> ) => {
        this.jobs = jobs;

        return jobs;
      });
  }

  sendSMS ( job : TransactionCenterJobModel ) : void {
    this.messengerService.sms({
      customer_id: job.job.customer.id,
      invoice_id : job.job.invoice.id,
      name       : job.job.customer.display_name,
      send_to    : job.job.customer.mobile
    })
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'));
  }

  viewJob ( job : TransactionCenterJobModel ) : void {
    this.router.stateService.go('app.invoice', {
      customer_id: job.job.invoice.customer_id,
      id         : job.job.invoice.id,
      vehicle_id : job.job.invoice.vehicle_id
    });
  }
}
